import { useMemo, useState, type ReactNode } from 'react';
import { formatCurrency, formatPercent } from '../SharedAccountBody';
import ComparisonCardSkeleton from './ComparisonCardSkeleton';
import ComparisonHoverTooltip from './ComparisonHoverTooltip';
import { isComparisonContentLoading } from './comparisonLoading';
import type { ComparisonAccount } from './comparisonTypes';
import { sharedHoldingRowTooltipContent } from './comparisonTooltipContent';
import {
  buildPositionDiff,
  type PositionDiffCell,
  type PositionDiffRow,
} from './comparisonUtils';

type SortMode = 'maxWeight' | 'spread' | 'symbol';

const CARD_CLASS =
  'rounded-[12px] border border-[color:var(--nq-line)] bg-[color:var(--nq-surface-card)] px-[18px] py-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]';

const TH_BASE =
  'border-b border-white/[0.08] px-2.5 py-2 align-top text-[0.78rem] font-semibold uppercase tracking-[0.04em] text-white/60 whitespace-nowrap';
const TH_LABEL = `${TH_BASE} text-left`;
const TH_NUM = `${TH_BASE} text-right tabular-nums`;
const TH_ACCOUNT = `${TH_NUM} overflow-hidden text-ellipsis`;
const TD_LABEL = 'px-2.5 py-2 align-top text-left';
const TD_NUM = 'px-2.5 py-2 align-top text-right tabular-nums whitespace-nowrap';
const TD_SUB = 'mt-px text-[0.78rem] text-white/55';
const TICKER = 'font-mono text-[0.85rem] font-semibold tracking-[0.02em]';
const TAG_BASE =
  'ml-1.5 inline-block whitespace-nowrap rounded-[6px] border px-[7px] py-0.5 text-[0.68rem] font-semibold uppercase leading-[1.3] tracking-[0.06em]';
const TAG_UNIQUE =
  'bg-[color:rgba(167,139,250,0.14)] border-[color:rgba(167,139,250,0.42)] text-[color:#c4b5fd]';
const TAG_PARTIAL =
  'bg-primary/12 border-primary/38 text-[color:#8bd6f1]';

const formatPlBadge = (cell: PositionDiffCell | undefined) => {
  if (!cell) return null;
  if (cell.unrealizedPLPct !== undefined) {
    const up = cell.unrealizedPLPct >= 0;
    return (
      <span
        className={`text-[0.78rem] font-medium ${up ? 'text-positive' : 'text-negative'}`}
      >
        {formatPercent(cell.unrealizedPLPct)}
      </span>
    );
  }
  if (cell.unrealizedPL !== undefined) {
    const up = cell.unrealizedPL >= 0;
    return (
      <span
        className={`text-[0.78rem] font-medium ${up ? 'text-positive' : 'text-negative'}`}
      >
        {up ? '+' : ''}
        {formatCurrency(cell.unrealizedPL)}
      </span>
    );
  }
  return null;
};

const PositionDiffRowTooltip = ({
  symbol,
  accounts,
  align = 'left',
  children,
}: {
  symbol: string;
  accounts: ComparisonAccount[];
  align?: 'left' | 'right';
  children: ReactNode;
}) => (
  <ComparisonHoverTooltip
    block
    wide
    placement="bottom"
    alignEnd={align === 'right'}
    content={sharedHoldingRowTooltipContent(symbol, accounts)}
  >
    {children}
  </ComparisonHoverTooltip>
);

interface Props {
  accounts: ComparisonAccount[];
}

const ComparisonPositionsDiff = ({ accounts }: Props) => {
  const [sortMode, setSortMode] = useState<SortMode>('maxWeight');
  const [showOnlyDifferences, setShowOnlyDifferences] = useState(false);

  const rows = useMemo(() => buildPositionDiff(accounts), [accounts]);
  const total = accounts.length;

  const filteredRows = useMemo(() => {
    let list: PositionDiffRow[] = rows;
    if (showOnlyDifferences) {
      list = list.filter(
        (r) => r.holdersCount < total || r.weightSpread >= 1,
      );
    }
    const sorted = [...list];
    sorted.sort((a, b) => {
      switch (sortMode) {
        case 'symbol':
          return a.symbol.localeCompare(b.symbol);
        case 'spread':
          return b.weightSpread - a.weightSpread;
        case 'maxWeight':
        default:
          return b.maxWeight - a.maxWeight;
      }
    });
    return sorted;
  }, [rows, sortMode, showOnlyDifferences, total]);

  if (isComparisonContentLoading(accounts)) {
    return <ComparisonCardSkeleton title="Positions diff" lines={7} />;
  }

  if (rows.length === 0) {
    return (
      <div className={CARD_CLASS}>
        <div className="mb-3 flex flex-col">
          <h3 className="text-[1.05rem] font-semibold text-foreground">
            Positions diff
          </h3>
        </div>
        <p className="my-3 text-[0.9rem] text-white/50">
          No open positions across selected portfolios.
        </p>
      </div>
    );
  }

  return (
    <div className={CARD_CLASS}>
      <div className="mb-3 flex flex-row flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-[1.05rem] font-semibold text-foreground">
            Positions diff
          </h3>
          <p className="mt-1 text-[0.85rem] text-white/55">
            Pivoted by symbol; cells show portfolio weight and unrealized P/L.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 text-[0.85rem] text-white/70">
          <label className="inline-flex cursor-pointer items-center gap-1.5">
            <input
              type="checkbox"
              checked={showOnlyDifferences}
              onChange={(e) => setShowOnlyDifferences(e.target.checked)}
            />
            Differences only
          </label>
          <label className="inline-flex items-center gap-1.5">
            Sort by
            <select
              className="cursor-pointer rounded-md border border-white/[0.12] bg-white/[0.04] px-2 py-1 text-[0.85rem] text-white/85 transition-colors hover:border-white/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value as SortMode)}
            >
              <option value="maxWeight">Largest weight</option>
              <option value="spread">Biggest spread</option>
              <option value="symbol">Symbol (A→Z)</option>
            </select>
          </label>
        </div>
      </div>

      <div className="overflow-x-auto overflow-y-visible [scrollbar-width:thin]">
        <table className="w-full min-w-[640px] table-auto border-collapse text-[14px]">
          <colgroup>
            <col className="w-[120px]" />
            <col className="w-[72px]" />
            <col className="w-[72px]" />
            {accounts.map((a) => (
              <col key={a.accountId} className="w-[110px] min-w-[100px]" />
            ))}
          </colgroup>
          <thead>
            <tr>
              <th className={TH_LABEL}>Symbol</th>
              <th className={TH_NUM}>Holders</th>
              <th className={TH_NUM}>Spread</th>
              {accounts.map((a) => (
                <th key={a.accountId} className={TH_ACCOUNT} title={a.label}>
                  <span className="inline-flex max-w-full items-center justify-end gap-1.5">
                    <span
                      className="inline-block h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: a.color }}
                      aria-hidden
                    />
                    <span className="ml-1 inline-block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-[0.78rem] normal-case tracking-normal">
                      {a.label}
                    </span>
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRows.map((row) => (
              <tr
                key={row.symbol}
                className="border-b border-white/[0.04] last:border-b-0"
              >
                <td className={TD_LABEL}>
                  <PositionDiffRowTooltip symbol={row.symbol} accounts={accounts}>
                    <div className="inline-flex items-center gap-2 whitespace-nowrap font-medium">
                      <span className={TICKER}>{row.symbol}</span>
                      {row.holdersCount < total && (
                        <span
                          className={`${TAG_BASE} ${
                            row.holdersCount === 1 ? TAG_UNIQUE : TAG_PARTIAL
                          }`}
                        >
                          {row.holdersCount === 1 ? 'unique' : 'partial'}
                        </span>
                      )}
                    </div>
                  </PositionDiffRowTooltip>
                </td>
                <td className={TD_NUM}>
                  {row.holdersCount}/{total}
                </td>
                <td className={TD_NUM}>
                  {row.weightSpread > 0
                    ? `${row.weightSpread.toFixed(1)} pp`
                    : '—'}
                </td>
                {accounts.map((a) => {
                  const cell = row.cells[a.accountId];
                  return (
                    <td
                      key={a.accountId}
                      className={`${TD_NUM}${cell ? '' : ' text-white/25'}`}
                    >
                      <PositionDiffRowTooltip
                        symbol={row.symbol}
                        accounts={accounts}
                        align="right"
                      >
                        <span className="inline-flex w-full items-center justify-between gap-2">
                          {cell ? (
                            <>
                              <div className="font-medium">
                                {cell.weightPct.toFixed(2)}%
                              </div>
                              <div className={TD_SUB}>{formatPlBadge(cell)}</div>
                            </>
                          ) : (
                            <span className="text-white/30">—</span>
                          )}
                        </span>
                      </PositionDiffRowTooltip>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ComparisonPositionsDiff;
